import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Bell, Check, MessageSquare, Shield, Zap } from 'lucide-react';
import { formatDate } from '../lib/utils';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('ds_token');
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const countRes = await axios.get(`${API_BASE}/api/notifications/count`, { headers });
      setUnread(countRes.data.unread || 0);
      if (open) {
        const listRes = await axios.get(`${API_BASE}/api/notifications?limit=10`, { headers });
        setNotifications(listRes.data.notifications || []);
      }
    } catch (err) {
      // Silent
    }
  }, [user, open]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markAllRead = async () => {
    const token = localStorage.getItem('ds_token');
    if (!token) return;
    try {
      await axios.post(`${API_BASE}/api/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {}
  };

  if (!user) return null;

  const typeIcons = {
    reply: MessageSquare,
    moderation: Shield,
    system: Zap,
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative w-9 h-9 p-0"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[hsl(var(--destructive))] text-[10px] text-white flex items-center justify-center font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[400px] overflow-y-auto bg-card border border-border rounded-xl shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Tout lire
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              Aucune notification
            </div>
          ) : (
            notifications.map((notif) => {
              const Icon = typeIcons[notif.type] || Bell;
              return (
                <div
                  key={notif._id}
                  className={`flex items-start gap-2 p-3 cursor-pointer hover:bg-[hsl(var(--muted))] border-b border-border/50 ${!notif.read ? 'bg-[hsl(var(--accent))]/20' : ''}`}
                  onClick={() => {
                    if (notif.link) window.location.href = notif.link;
                    setOpen(false);
                  }}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    notif.type === 'moderation' ? 'bg-red-100 text-red-600' : 'bg-[hsl(var(--accent))] text-[hsl(var(--primary))]'
                  }`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium leading-tight">{notif.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{notif.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{formatDate(notif.created_at)}</p>
                  </div>
                  {!notif.read && <div className="w-2 h-2 rounded-full bg-[hsl(var(--primary))] shrink-0 mt-1" />}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
